/**
 * Enhancement plugin system for connector-specific field behaviors.
 *
 * Enhancements are additional UI elements rendered alongside specific fields
 * for specific connector types. For example, Google Ads has an "Account Discovery"
 * button that appears after the OAuth field.
 *
 * This keeps connector-specific logic out of the core dynamic form renderer.
 */
import React from 'react';
import { PreviewMatchingSheetsButton } from './PreviewMatchingSheetsButton';
import { YahooAdvertiserScopeNotice } from './YahooAdvertiserScopeNotice';

export interface EnhancementProps {
  connectorType: string;
  fieldName: string;
  formValues: Record<string, unknown>;
  onFieldChange: (fieldName: string, value: unknown) => void;
  /** Editor mode (#501): saved connector name. When set, the preview resolves
   *  stored credentials server-side instead of sending inline credentials. */
  connectorName?: string;
}

/**
 * Registry of field enhancements keyed by connector type and field name.
 * Structure: { [connectorType]: { [fieldName]: Component } }
 */
export const fieldEnhancements: Record<
  string,
  Record<string, React.ComponentType<EnhancementProps>>
> = {
  google_sheets: {
    owner_email: PreviewMatchingSheetsButton,
  },
  // #2434: the notice for a configuration stored with the withdrawn scope.
  yahoo_dsp: {
    advertiser_scope: YahooAdvertiserScopeNotice,
  },
};

/**
 * Get the enhancement component for a specific connector type and field.
 * Returns null if no enhancement exists.
 */
export function getFieldEnhancement(
  connectorType: string,
  fieldName: string
): React.ComponentType<EnhancementProps> | null {
  return fieldEnhancements[connectorType]?.[fieldName] ?? null;
}
